import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import api, { formatApiError } from "@/lib/api";

const today = () => new Date().toISOString().slice(0, 10);

// mode: "edit" | "view". initial = existing forms. onSubmit(forms)
export default function RegistrationForm({ open, onOpenChange, childName, initial, mode = "edit", onSubmit }) {
  const [waiverText, setWaiverText] = useState("");
  const [f, setF] = useState({});
  const [saving, setSaving] = useState(false);
  const view = mode === "view";

  useEffect(() => { api.get("/content").then((r) => setWaiverText(r.data.waiver_text || "")).catch(() => {}); }, []);
  useEffect(() => { if (open) setF({ photo_permission: true, ...(initial || {}) }); }, [open, initial]);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    if (!f.guardian_name) return toast.error("Parent/guardian name is required.");
    if (!f.emergency_name || !f.emergency_phone) return toast.error("An emergency contact name and phone are required.");
    if (!f.agree_waiver) return toast.error("Please agree to the liability waiver to continue.");
    if (!f.agree_cancellation) return toast.error("Please agree to the cancellation policy.");
    if (!f.signature) return toast.error("Please type your full name as your signature.");
    setSaving(true);
    try {
      await onSubmit({ ...f, signed_date: f.signed_date || today() });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const field = (label, key, opts = {}) => (
    <div>
      <Label>{label}{opts.req && <span className="text-coral"> *</span>}</Label>
      {opts.area
        ? <Textarea value={f[key] || ""} readOnly={view} onChange={(e) => set(key, e.target.value)} data-testid={`reg-${key}`} className="mt-1 bg-white read-only:bg-cream read-only:text-navy" placeholder={opts.ph} />
        : <Input value={f[key] || ""} readOnly={view} onChange={(e) => set(key, e.target.value)} data-testid={`reg-${key}`} className="mt-1 bg-white read-only:bg-cream read-only:text-navy" placeholder={opts.ph} />}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-cream max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-serif text-2xl text-navy">{view ? "Signed Registration & Waiver" : "Registration & Liability Waiver"}{childName ? ` — ${childName}` : ""}</DialogTitle></DialogHeader>

        <div className="rounded-xl bg-coral/10 border border-coral/30 p-3 text-xs text-navy/80">
          Emergency notice: This website is <strong>not monitored for emergencies</strong>. In an emergency, call 911.
        </div>

        <p className="font-serif text-lg text-navy mt-2">Parent / Guardian</p>
        <div className="grid sm:grid-cols-2 gap-3">
          {field("Full name", "guardian_name", { req: true })}
          {field("Phone", "guardian_phone")}
          {field("Relationship to child", "guardian_relationship", { ph: "e.g. Mother" })}
        </div>

        <p className="font-serif text-lg text-navy mt-3">Emergency Contact</p>
        <div className="grid sm:grid-cols-2 gap-3">
          {field("Contact name", "emergency_name", { req: true })}
          {field("Contact phone", "emergency_phone", { req: true })}
        </div>

        <p className="font-serif text-lg text-navy mt-3">About Your Child</p>
        <div className="grid gap-3">
          {field("Medical concerns", "medical", { area: true, ph: "Any conditions we should know about" })}
          {field("Allergies", "allergies", { area: true })}
          {field("Swimming experience", "swim_experience", { area: true })}
          {field("Water fears", "water_fears", { area: true })}
          {field("Special needs or accommodations", "special_needs", { area: true })}
        </div>

        <label className="flex items-center gap-3 mt-3 text-sm text-navy/80">
          <Checkbox checked={!!f.photo_permission} disabled={view} onCheckedChange={(v) => set("photo_permission", !!v)} data-testid="reg-photo_permission" />
          <span>I give permission for photos/videos of my child to be used by Float Now.</span>
        </label>

        <p className="font-serif text-lg text-navy mt-3">Liability Waiver</p>
        <div className="rounded-xl border border-gold/40 bg-white p-4 max-h-52 overflow-y-auto text-sm text-navy/80 whitespace-pre-line" data-testid="reg-waiver-text">{waiverText}</div>

        <label className="flex items-start gap-3 mt-3 text-sm text-navy/80">
          <Checkbox checked={!!f.agree_waiver} disabled={view} onCheckedChange={(v) => set("agree_waiver", !!v)} data-testid="reg-agree_waiver" className="mt-0.5" />
          <span>I have read and agree to the liability waiver above, and I understand no child is ever drown-proof.<span className="text-coral"> *</span></span>
        </label>
        <label className="flex items-start gap-3 mt-2 text-sm text-navy/80">
          <Checkbox checked={!!f.agree_cancellation} disabled={view} onCheckedChange={(v) => set("agree_cancellation", !!v)} data-testid="reg-agree_cancellation" className="mt-0.5" />
          <span>I agree to the cancellation policy.<span className="text-coral"> *</span></span>
        </label>

        <div className="grid sm:grid-cols-2 gap-3 mt-3">
          <div><Label>Electronic signature (type full name)<span className="text-coral"> *</span></Label><Input value={f.signature || ""} readOnly={view} onChange={(e) => set("signature", e.target.value)} data-testid="reg-signature" className="mt-1 bg-white read-only:bg-cream read-only:text-navy" /></div>
          <div><Label>Date</Label><Input type="date" value={f.signed_date || today()} readOnly={view} onChange={(e) => set("signed_date", e.target.value)} data-testid="reg-signed_date" className="mt-1 bg-white read-only:bg-cream read-only:text-navy" /></div>
        </div>

        {!view && (
          <Button onClick={submit} disabled={saving} data-testid="reg-submit" className="mt-4 w-full rounded-full bg-coral hover:bg-[#C95B50] text-white">
            {saving ? "Saving…" : "Sign & Save Registration"}
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
}
